// src/features/affiliate/admin/TiersTab.tsx
// Admin: commission tier ladder — qualification thresholds and payout
// multipliers that ChangeAffiliateTier assigns partners into.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useAffiliateCommissionTier, useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import type { CommissionTier } from '@core/types/affiliate';
import { TierStatusBadge } from '../badges';
import { errMessage, listParams } from '../format';

export const TiersTab = observer(() => {
  const { t } = useI18n();
  const store = useAffiliateCommissionTier();

  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CommissionTier | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [level, setLevel] = useState('0');
  const [isDefault, setIsDefault] = useState(false);
  const [minConversions, setMinConversions] = useState('0');
  const [minActiveDays, setMinActiveDays] = useState('0');
  const [payoutMultiplier, setPayoutMultiplier] = useState('1.0');
  const [color, setColor] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = () => {
    setError(null);
    store.ListCommissionTiers(listParams({ limit: 100 })).catch((e: unknown) => setError(errMessage(e, t('affiliate.admin.tiers.loadError'))));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDialog = async (tier: CommissionTier | null) => {
    let full = tier;
    if (tier?.entity?.id) {
      try { full = await store.GetCommissionTier(tier.entity.id); } catch { full = tier; }
    }
    setEditing(full);
    setName(full?.name ?? '');
    setDescription(full?.description ?? '');
    setSlug(full?.slug ?? '');
    setLevel(String(full?.level ?? 0));
    setIsDefault(full?.is_default ?? false);
    setMinConversions(String(full?.min_conversions ?? 0));
    setMinActiveDays(String(full?.min_active_days ?? 0));
    setPayoutMultiplier(full?.payout_multiplier ?? '1.0');
    setColor(full?.color ?? '');
    setDialogOpen(true);
  };

  const save = async () => {
    if (!name.trim() || !slug.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      if (editing?.entity) {
        await store.UpdateCommissionTier(editing.entity.id, {
          entity: editing.entity,
          update_mask: ['name', 'description', 'level', 'is_default', 'min_conversions', 'min_active_days', 'payout_multiplier', 'color'],
          name: name.trim(),
          description,
          level: Number(level) || 0,
          is_default: isDefault,
          min_conversions: Number(minConversions) || 0,
          min_active_days: Number(minActiveDays) || 0,
          payout_multiplier: payoutMultiplier,
          icon: editing.icon,
          color,
          metadata: editing.metadata ?? {},
        });
      } else {
        await store.CreateCommissionTier({
          entity: undefined,
          name: name.trim(),
          description,
          slug: slug.trim(),
          level: Number(level) || 0,
          is_default: isDefault,
          min_conversions: Number(minConversions) || 0,
          min_revenue: undefined,
          min_active_days: Number(minActiveDays) || 0,
          payout_multiplier: payoutMultiplier,
          icon: '',
          color,
          metadata: {},
        });
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.tiers.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => openDialog(null)}>{t('affiliate.admin.tiers.new')}</Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {store.isLoading && store.items.length === 0 && <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy="true" />}

      <div className="space-y-2">
        {store.items
          .slice()
          .sort((a, b) => a.level - b.level)
          .map((tr) => (
            <div key={tr.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {tr.color && <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: tr.color }} aria-hidden="true" />}
                  <span className="font-medium text-foreground">{tr.name}</span>
                  <TierStatusBadge status={tr.status} />
                  {tr.is_default && <Badge variant="secondary">{t('affiliate.admin.tiers.default')}</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('affiliate.admin.tiers.qualification', { conversions: tr.min_conversions, days: tr.min_active_days })} · ×{tr.payout_multiplier}
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => openDialog(tr)}>{t('common.edit')}</Button>
            </div>
          ))}
      </div>

      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={save}
        submitLabel={editing ? t('common.saveChanges') : t('common.create')}
        canSubmit={!!name.trim() && (!!editing || !!slug.trim())}
        isSubmitting={isSaving}
        dirty={!!(name.trim() || description.trim())}
        width="md"
        aria-label={editing ? t('affiliate.admin.tiers.editTitle') : t('affiliate.admin.tiers.new')}
      >
        <div className="px-3.5 pb-1 pt-2">
          <TitleInput value={name} onChange={setName} placeholder={`${t('common.name')}…`} initialFocus aria-label={t('common.name')} />
          <AutoGrowTextarea value={description} onChange={setDescription} placeholder={`${t('common.description')}…`} minRows={1} />
        </div>
        <div className="space-y-1 px-3.5 pb-3">
          {!editing && (
            <FormRow label={t('affiliate.admin.offers.slug')} labelWidth="sm">
              <input value={slug} onChange={(e) => setSlug(e.target.value)} className="w-full max-w-xs rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
          )}
          <div className="flex flex-wrap gap-2">
            {[
              { label: t('affiliate.admin.tiers.level'), value: level, set: setLevel },
              { label: t('affiliate.admin.tiers.minConversions'), value: minConversions, set: setMinConversions },
              { label: t('affiliate.admin.tiers.minActiveDays'), value: minActiveDays, set: setMinActiveDays },
            ].map((f) => (
              <span key={f.label} className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
                <span className="text-xs text-muted-foreground">{f.label}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={f.value}
                  onChange={(e) => f.set(e.target.value.replace(/[^0-9]/g, ''))}
                  aria-label={f.label}
                  className="w-12 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
                />
              </span>
            ))}
          </div>
          <FormRow label={t('affiliate.admin.tiers.payoutMultiplier')} labelWidth="sm">
            <input
              type="text"
              inputMode="decimal"
              value={payoutMultiplier}
              onChange={(e) => setPayoutMultiplier(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="1.0"
              className="w-20 rounded-md border border-input bg-background px-2.5 py-1.5 text-right text-sm tabular-nums text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('affiliate.admin.tiers.color')} labelWidth="sm">
            <span className="inline-flex items-center gap-2">
              <input
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#4f46e5"
                aria-label={t('affiliate.admin.tiers.color')}
                className="w-28 rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
              />
              {color && <span className="h-5 w-5 shrink-0 rounded-full border border-border" style={{ backgroundColor: color }} aria-hidden="true" />}
            </span>
          </FormRow>
          <FormRow label={t('affiliate.admin.tiers.isDefault')} labelWidth="sm">
            <ToggleSwitch label="" checked={isDefault} onChange={setIsDefault} />
          </FormRow>
        </div>
      </FormDialog>
    </div>
  );
});
